import { createContext, useContext, useEffect, useReducer, type Dispatch, type ReactNode } from "react";
import { kwhYear } from "@biloplader/calc";

export type Setup = "box" | "none";
export type Era = "now" | "2027";
export type Area = "DK1" | "DK2";

export interface WizardState {
  step: 0 | 1 | 2 | 3;
  km: number;
  wh: number;
  ude: number;
  night: boolean;
  setup: Setup;
  era: Era;
  area: Area;
}

export const initialState: WizardState = {
  step: 0,
  km: 15000,
  wh: 180,
  ude: 15,
  night: true,
  setup: "box",
  era: "now",
  area: "DK1",
};

type Action =
  | { type: "set"; field: "km" | "wh" | "ude"; value: number }
  | { type: "night"; value: boolean }
  | { type: "setup"; value: Setup }
  | { type: "era"; value: Era }
  | { type: "area"; value: Area }
  | { type: "step"; value: WizardState["step"] }
  | { type: "reset" };

function reducer(s: WizardState, a: Action): WizardState {
  switch (a.type) {
    case "set": return { ...s, [a.field]: a.value };
    case "night": return { ...s, night: a.value };
    case "setup": return { ...s, setup: a.value };
    case "era": return { ...s, era: a.value };
    case "area": return { ...s, area: a.value };
    case "step": return { ...s, step: a.value };
    case "reset": return { ...initialState };
  }
}

const Ctx = createContext<{ state: WizardState; dispatch: Dispatch<Action> } | null>(null);

/** Where the in-progress answers survive a reload. Session-scoped on purpose:
 * these are the inputs to one sitting, not a preference worth remembering next
 * week. Switching language is a real navigation (the URL carries the language),
 * so without this a toggle mid-form would throw the answers away. */
const WIZARD_STORAGE = "wizard";

function loadState(): WizardState {
  try {
    const raw = sessionStorage.getItem(WIZARD_STORAGE);
    if (!raw) return initialState;
    // Merge over the defaults so a stored shape from an older build, or a
    // hand-edited value, can never leave a field undefined.
    return { ...initialState, ...(JSON.parse(raw) as Partial<WizardState>) };
  } catch {
    return initialState;
  }
}

export function WizardProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, loadState);

  useEffect(() => {
    try {
      sessionStorage.setItem(WIZARD_STORAGE, JSON.stringify(state));
    } catch {
      /* ignore */
    }
  }, [state]);

  return <Ctx.Provider value={{ state, dispatch }}>{children}</Ctx.Provider>;
}

export function useWizard() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useWizard must be used within WizardProvider");
  return ctx;
}

/** Date the comparison applies to, derived from the era toggle. */
export const eraDate = (era: Era): string | undefined => (era === "2027" ? "2027-06-01" : undefined);

/** kWh/year from the current profile — via the shared engine (single source of truth). */
export const kwhYearOf = (s: WizardState) => kwhYear({ kmAar: s.km, whKm: s.wh });
